<template>
    <div class="thankyou-page py-6">
        <div class="thankyou-container container !max-w-[900px]">
            <div class="flex items-center -mx-3">
                <div class="w-full max-w-full px-3">
                    <!-- Success Header -->
                    <div class="success-header w-full mb-6 relative text-center">
                        <div class="success-icon mb-5 mx-auto">
                            <svg class="inline-block" width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="64" height="64" rx="32" fill="#28BD66"/><path d="M32.0007 48.6668C41.2054 48.6668 48.6673 41.2049 48.6673 32.0002C48.6673 22.7954 41.2054 15.3335 32.0007 15.3335C22.7959 15.3335 15.334 22.7954 15.334 32.0002C15.334 41.2049 22.7959 48.6668 32.0007 48.6668Z" stroke="white" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M27 32L30.3333 35.3333L37 28.6666" stroke="white" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </div>
                        <h1 class="success-title text-2xl lg:text-[32px] text-[var(--charcoal)] font-bold mb-2">Booking Confirmed!</h1>
                        <p class="success-subtitle text-lg text-[var(--tone-gray)] mb-6 lg:mb-8">Your adventure awaits! We've sent a confirmation email with all the details.</p>
                    </div>

                    <div v-if="tourBooking.data" class="confirmation-content mt-6">
                        <!-- Customer Information Card -->
                        <div class="info-card customer-card w-full p-3 sm:p-6 bg-white border border-[var(--bone-dark)] rounded-lg relative">
                            <h2 class="card-title text-xl text-[var(--charcoal)] font-bold mb-4">
                                <svg class="inline-block mr-3" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.8327 17.5V15.8333C15.8327 14.9493 15.4815 14.1014 14.8564 13.4763C14.2312 12.8512 13.3834 12.5 12.4993 12.5H7.49935C6.61529 12.5 5.76745 12.8512 5.14233 13.4763C4.5172 14.1014 4.16602 14.9493 4.16602 15.8333V17.5" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.99935 9.16667C11.8403 9.16667 13.3327 7.67428 13.3327 5.83333C13.3327 3.99238 11.8403 2.5 9.99935 2.5C8.1584 2.5 6.66602 3.99238 6.66602 5.83333C6.66602 7.67428 8.1584 9.16667 9.99935 9.16667Z" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                Hello {{ customer.name }}
                            </h2>
                            <p class="card-subtitle text-base text-[var(--tone-gray)] mb-4">Thank you for your booking. Here are your contact details:</p>

                            <div class="contact-grid">
                                <div class="contact-item flex items-center mb-3">
                                    <div class="contact-icon mr-3">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                    </div>
                                    <div class="contact-details flex items-center text-base text-[var(--charcoal-dark)]">
                                        <div class="contact-label font-semibold mr-2">Full Name</div>
                                        <div class="contact-value">{{ customer.name }}</div>
                                    </div>
                                </div>

                                <div class="contact-item flex items-center mb-3">
                                    <div class="contact-icon mr-3">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                            <polyline points="22,6 12,13 2,6"></polyline>
                                        </svg>
                                    </div>
                                    <div class="contact-details flex items-center text-base text-[var(--charcoal-dark)]">
                                        <div class="contact-label font-semibold mr-2">Email Address</div>
                                        <div class="contact-value">{{ customer.email }}</div>
                                    </div>
                                </div>

                                <div class="contact-item flex items-center">
                                    <div class="contact-icon mr-3">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                        </svg>
                                    </div>
                                    <div class="contact-details flex items-center text-base text-[var(--charcoal-dark)]">
                                        <div class="contact-label font-semibold mr-2">Phone Number</div>
                                        <div class="contact-value">{{ customer.phone_number }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Booking Summary Section -->
                        <div class="section-header mt-6">
                            <h2 class="section-title text-lg lg:text-2xl text-[var(--charcoal)] font-bold mb-4">Your Booking Summary</h2>
                        </div>

                        <!-- Booking Cards -->
                        <div v-for="(booking, index) in tourBooking.data" :key="booking.id" class="booking-card w-full p-3 sm:p-6 bg-white border border-[var(--bone-dark)] rounded-lg relative mb-6">
                            <!-- <div class="booking-header"> -->
                            <div class="booking-header cursor-pointer sm:flex justify-between items-center" @click="toggleAccordion(index)">
                                <h3 class="booking-package-name text-xl text-[var(--charcoal)] font-bold flex items-start justify-between"><span>{{ booking.package_name }}</span> <span class="min-[640px]:hidden pt-1"><svg :class="{ 'rotate-180': openAccordions[index] }" class="w-6 h-6 transition-transform duration-300 ml-2" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" /></svg></span></h3>
                                <div class="flex items-center max-[639px]:justify-between max-[639px]:mt-2">
                                    <div class="booking-id-badge inline-block text-sm text-[#8C7B73] bg-[#F9F5F1] rounded-2xl border border-[var(--bone)] px-4 py-2 mr-4">
                                        Booking ID #{{ booking.id }}
                                    </div>
                                    <svg :class="{ 'rotate-180': openAccordions[index] }" class="w-6 h-6 transition-transform duration-300 max-[639px]:hidden" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" /></svg>
                                </div>
                            </div>
                            <transition name="accordion">
                                <div
                                    v-show="openAccordions[index]"
                                    class="pt-3 sm:pt-6"
                                >
                                    <div class="booking-details-grid mb-3 pb-3 border-b border-[var(--bone-dark)]">
                                        <div class="flex flex-wrap -mx-3">
                                            <div class="detail-item flex items-center mb-1 w-full md:w-1/2 max-w-full px-3">
                                                <div class="detail-icon mr-3"><svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="12" fill="#FFF7EB"/><path d="M16.666 11.6665V14.9998" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M23.334 11.6665V14.9998" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M25.8333 13.3333H14.1667C13.2462 13.3333 12.5 14.0795 12.5 15V26.6666C12.5 27.5871 13.2462 28.3333 14.1667 28.3333H25.8333C26.7538 28.3333 27.5 27.5871 27.5 26.6666V15C27.5 14.0795 26.7538 13.3333 25.8333 13.3333Z" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M12.5 18.3333H27.5" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
                                                <div class="detail-content">
                                                    <div class="detail-label text-xs text-[#8C7B73]">Date</div>
                                                    <div class="detail-value text-sm font-bold text-[var(--charcoal-dark)]">{{ booking.date }}</div>
                                                </div>
                                            </div>

                                            <div v-if="booking.package_has_slots" class="detail-item flex items-center mb-1 w-full md:w-1/2 max-w-full px-3">
                                                <div class="detail-icon mr-3"><svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="12" fill="#FFF7EB"/><path d="M19.9993 28.3332C24.6017 28.3332 28.3327 24.6022 28.3327 19.9998C28.3327 15.3975 24.6017 11.6665 19.9993 11.6665C15.397 11.6665 11.666 15.3975 11.666 19.9998C11.666 24.6022 15.397 28.3332 19.9993 28.3332Z" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 15V20L23.3333 21.6667" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
                                                <div class="detail-content">
                                                    <div class="detail-label text-xs text-[#8C7B73]">Time</div>
                                                    <div class="detail-value text-sm font-bold text-[var(--charcoal-dark)]">{{ booking.time }}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="booking.hotel_id" class="detail-item full-width flex items-center">
                                            <div class="detail-icon mr-3"><svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="12" fill="#FFF7EB"/><path d="M19.9993 28.3332C24.6017 28.3332 28.3327 24.6022 28.3327 19.9998C28.3327 15.3975 24.6017 11.6665 19.9993 11.6665C15.397 11.6665 11.666 15.3975 11.666 19.9998C11.666 24.6022 15.397 28.3332 19.9993 28.3332Z" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 15V20L23.3333 21.6667" stroke="#EE7C2B" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
                                            <div class="detail-content">
                                                <div class="detail-label text-xs text-[#8C7B73]">Hotel Pickup</div>
                                                <div class="detail-value text-sm font-bold text-[var(--charcoal-dark)]">{{ booking.hotel_name }}</div>
                                                <div class="detail-address text-sm font-bold text-[var(--charcoal-dark)]">{{ booking.hotel_address }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Guests Table -->
                                    <h4 class="guests-title flex items-center text-base text-[var(--charcoal-dark)] font-semibold mb-3"><svg class="mr-2" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3327 17.5V15.8333C13.3327 14.9493 12.9815 14.1014 12.3564 13.4763C11.7313 12.8512 10.8834 12.5 9.99935 12.5H4.99935C4.11529 12.5 3.26745 12.8512 2.64233 13.4763C2.01721 14.1014 1.66602 14.9493 1.66602 15.8333V17.5" stroke="#8C7B73" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.49935 9.16667C9.3403 9.16667 10.8327 7.67428 10.8327 5.83333C10.8327 3.99238 9.3403 2.5 7.49935 2.5C5.6584 2.5 4.16602 3.99238 4.16602 5.83333C4.16602 7.67428 5.6584 9.16667 7.49935 9.16667Z" stroke="#8C7B73" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.334 17.5001V15.8334C18.3334 15.0948 18.0876 14.3774 17.6351 13.7937C17.1826 13.2099 16.5491 12.793 15.834 12.6084" stroke="#8C7B73" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.334 2.6084C14.051 2.79198 14.6865 3.20898 15.1403 3.79366C15.5942 4.37833 15.8405 5.09742 15.8405 5.83757C15.8405 6.57771 15.5942 7.2968 15.1403 7.88147C14.6865 8.46615 14.051 8.88315 13.334 9.06673" stroke="#8C7B73" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg> Guests ({{ booking.tourists }} total)</h4>
                                    <div v-for="rate in booking.tourPackageRates" :key="rate.id" class="guests-row w-full flex items-start justify-between bg-[var(--primary-light)] rounded-2xl p-4 mb-4">
                                        <div class="w-full">
                                            <div class="col-type text-base text-[var(--charcoal-dark)]">{{ rate.tourists }}</div>
                                            <div class="col-rate text-sm text-[var(--tone-gray)]">{{ currencyFormat(rate.rate) }}</div>
                                            <div class="sm:flex items-center">
                                                <div v-if="booking.is_group_rate_enabled == 0" class="col-permit text-xs text-[var(--tone-gray)] mt-2 mr-2">Permit Fee: {{ currencyFormat(rate.permit_fee) }}</div>
                                                <div v-if="booking.is_group_rate_enabled == 0" class="col-fees text-xs text-[var(--tone-gray)] mt-2 mr-2">Tax: {{ currencyFormat(rate.additional_charge) }}</div>
                                            </div>
                                        </div>
                                        <div class="col-total text-base font-bold whitespace-nowrap">{{ currencyFormat(rate.total) }}</div>
                                    </div>

                                    <!-- Pricing Breakdown -->
                                    <div class="pricing-breakdown border-t border-[var(--bone)] pt-4">
                                        <div class="pricing-row flex items-center justify-between text-sm text-[#8C7B73] mb-2">
                                            <span>{{ booking.discount2_value > 0 ? 'Ticket Cost' : 'Subtotal' }}</span>
                                            <span class="text-[#3A312C]">{{ currencyFormat(booking.subtotal) }}</span>
                                        </div>

                                        <div v-if="booking.discount2_value > 0" class="pricing-row discount-row flex items-center justify-between text-sm text-[#8C7B73] mb-2">
                                            <span>
                                                Discount
                                                <!-- <span v-if="booking.discount2_percentage > 0" class="discount-percent">({{ booking.discount2_percentage }}%)</span> -->
                                            </span>
                                            <span class="discount-amount text-[#3A312C]">-{{ currencyFormat(booking.discount2_value) }}</span>
                                        </div>

                                        <div v-if="booking.discount2_value > 0" class="pricing-row flex items-center justify-between text-sm text-[#8C7B73] mb-2">
                                            <span>Subtotal After Discount</span>
                                            <span class="text-[#3A312C]">{{ currencyFormat(booking.subtotal - booking.discount2_value) }}</span>
                                        </div>

                                        <div v-if="booking.tourBookingAddons?.length" class="addons-section border-t border-b border-[var(--bone)] my-2 py-2">
                                            <div class="addons-header font-semibold">Add-ons:</div>
                                            <div v-for="addon in booking.tourBookingAddons" :key="addon.id" class="pricing-row addon-row flex items-center justify-between text-[#8C7B73]">
                                                <span>{{ addon.name }}</span>
                                                <span class="text-[#3A312C]">{{ currencyFormat(addon.price) }}</span>
                                            </div>
                                        </div>

                                        <div class="pricing-row flex items-center justify-between text-sm text-[#8C7B73] mb-2">
                                            <span>Booking Fees</span>
                                            <span class="text-[#3A312C]">{{ currencyFormat(Number(booking.service_commission) + Number(booking.addons_fee)) }}</span>
                                        </div>

                                        <div class="pricing-total flex items-center justify-between text-base font-bold text-[#3A312C] border-t border-[var(--bone)] my-2 py-2">
                                            <span>Booking Total</span>
                                            <span class="text-[#EE7C2B]">{{ currencyFormat((Number(booking.subtotal) - Number(booking.discount2_value)) + Number(booking.service_commission) + Number(booking.addons_total) + Number(booking.addons_fee)) }}</span>
                                        </div>
                                        <div class="tax-note flex items-center justify-end text-xs text-[var(--charcoal-dark)]">(inclusive all taxes)</div>
                                    </div>
                                </div>
                            </transition>
                        </div>

                        <!-- Overall Pricing Summary for Combo Packages -->
                        <div v-if="isComboPackage && tourBooking.data.length > 1" class="overall-pricing-summary w-full p-4 sm:p-6 bg-white border border-[var(--bone-dark)] rounded-lg mb-6">
                            <h2 class="section-title text-xl text-[var(--charcoal)] font-bold mb-4">Total Booking Summary</h2>
                            
                            <div class="pricing-breakdown space-y-3">
                                <!-- Subtotal -->
                                <div class="pricing-row flex items-center justify-between text-sm">
                                    <span class="text-[#8C7B73]">Subtotal (All Packages)</span>
                                    <span class="font-semibold text-[#3A312C]">{{ currencyFormat(overallPricing.subtotal) }}</span>
                                </div>

                                <!-- Discount -->
                                <div v-if="overallPricing.discount > 0" class="pricing-row discount-row flex items-center justify-between text-sm border-t border-[var(--bone)] pt-3">
                                    <span class="text-[#8C7B73]">Discount</span>
                                    <span class="font-semibold text-[#3A312C]">-{{ currencyFormat(overallPricing.discount) }}</span>
                                </div>

                                <!-- Subtotal After Discount -->
                                <div v-if="overallPricing.discount > 0" class="pricing-row flex items-center justify-between text-sm">
                                    <span class="text-[#8C7B73]">Subtotal After Discount</span>
                                    <span class="font-semibold text-[#3A312C]">{{ currencyFormat(overallPricing.subtotal - overallPricing.discount) }}</span>
                                </div>

                                <!-- Add-ons -->
                                <div v-if="overallPricing.addonsTotal > 0" class="pricing-row addons-section border-t border-[var(--bone)] pt-3">
                                    <div class="flex items-center justify-between text-sm w-full">
                                        <span class="text-[#8C7B73]">Add-ons (All Packages)</span>
                                        <span class="font-semibold text-[#3A312C]">{{ currencyFormat(overallPricing.addonsTotal) }}</span>
                                    </div>
                                </div>

                                <!-- Fees -->
                                <div class="pricing-row flex items-center justify-between text-sm border-t border-[var(--bone)] pt-3">
                                    <span class="text-[#8C7B73]">Booking Fees</span>
                                    <span class="font-semibold text-[#3A312C]">{{ currencyFormat(overallPricing.fees) }}</span>
                                </div>

                                <!-- Total -->
                                <div class="pricing-total flex items-center justify-between text-lg font-bold border-t border-[var(--bone)] mt-4 pt-4">
                                    <span class="text-[#3A312C]">Total Amount</span>
                                    <span class="text-[#EE7C2B] text-xl">{{ currencyFormat(overallPricing.total) }}</span>
                                </div>
                                <div class="tax-note flex items-center justify-end text-xs text-[var(--charcoal-dark)]">(inclusive all taxes)</div>
                            </div>
                        </div>

                        <!-- Help Section -->
                        <div v-if="tourBooking?.data[0]?.company_number" class="help-card flex items-start w-full p-4 border border-[var(--bone-dark)] rounded-lg relative">
                            <div class="help-icon mr-4 pt-1">
                                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="20" fill="#007BFF" fill-opacity="0.1"/><path d="M28.3332 24.1V26.6C28.3341 26.8321 28.2866 27.0618 28.1936 27.2745C28.1006 27.4871 27.9643 27.678 27.7933 27.8349C27.6222 27.9918 27.4203 28.1112 27.2005 28.1856C26.9806 28.26 26.7477 28.2876 26.5165 28.2667C23.9522 27.988 21.489 27.1118 19.3249 25.7083C17.3114 24.4289 15.6043 22.7218 14.3249 20.7083C12.9165 18.5343 12.0401 16.0592 11.7665 13.4833C11.7457 13.2529 11.7731 13.0206 11.8469 12.8014C11.9208 12.5821 12.0395 12.3806 12.1955 12.2097C12.3515 12.0388 12.5414 11.9023 12.753 11.8088C12.9647 11.7153 13.1935 11.6669 13.4249 11.6667H15.9249C16.3293 11.6627 16.7214 11.8059 17.028 12.0696C17.3346 12.3333 17.5349 12.6995 17.5915 13.1C17.697 13.9001 17.8927 14.6856 18.1749 15.4417C18.287 15.7399 18.3112 16.0641 18.2448 16.3757C18.1783 16.6874 18.0239 16.9734 17.7999 17.2L16.7415 18.2583C17.9278 20.3446 19.6552 22.072 21.7415 23.2583L22.7999 22.2C23.0264 21.976 23.3125 21.8216 23.6241 21.7551C23.9358 21.6886 24.2599 21.7129 24.5582 21.825C25.3143 22.1071 26.0998 22.3028 26.8999 22.4083C27.3047 22.4655 27.6744 22.6693 27.9386 22.9813C28.2029 23.2932 28.3433 23.6913 28.3332 24.1Z" stroke="#007BFF" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </div>
                            <div class="help-content">
                                <h3 class="help-title text-lg text-[var(--charcoal)] font-bold mb-1">Need Help?</h3>
                                <p class="help-text text-sm text-[var(--tone-gray)]">
                                    For any questions or assistance, please call us at:
                                    <a :href="'tel:' + tourBooking.data[0].company_number" class="help-phone text-[#007BFF]">
                                        {{ tourBooking.data[0].company_number }}
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import { formatCurrencyIntl } from '../utils/currency';
import { getMomentTimezone } from '../utils/dateUtils';

export default {
    name: "Thankyou",
    title: "Booking Confirmation - Native American Tours",
    data() {
        return {
            tenantId: 0,
            iframeStatus: 0,
            id: [],
            idLength: 0,
            tourBooking: {
                details: [],
                baseUrl: process.env.VUE_APP_BASE_URL,
                data: [],
            },
            customer: {},
            openAccordions: [],
            affiliate_id: 0,
            with_rate_groups: 1,
            hotel_id: 0,
            selectedHotel: null
        };
    },
    computed: {
        isComboPackage() {
            return this.$store.state.comboIds && this.$store.state.comboIds !== 0;
        },
        overallPricing() {
            const data = this.tourBooking.data || [];
            let subtotal = 0;
            let discount = 0;
            let addonsTotal = 0;
            let fees = 0;

            data.forEach(booking => {
                subtotal += Number(booking.subtotal) || 0;
                discount += Number(booking.discount2_value) || 0;
                addonsTotal += Number(booking.addons_total) || 0;
                fees += (Number(booking.service_commission) || 0) + (Number(booking.addons_fee) || 0);
            });

            const total = (subtotal - discount) + addonsTotal + fees;

            return {
                subtotal,
                discount,
                addonsTotal,
                fees,
                total
            };
        }
    },
    created() {
        // Clear all booking-related data from store
        this.$store.dispatch('storeCartItem', {});
        this.$store.dispatch('storeSlotId', 0);
        this.$store.dispatch('storeTimeDate', null);
        this.$store.dispatch('storeRateGroupCounters', {});
        this.$store.dispatch('storeFormData', null);
        
        // Clear URL parameters from localStorage
        const urlParams = JSON.parse(localStorage.getItem('urlParams') || '{}');
        if (urlParams.timeSlot || urlParams.rateGroupIds || urlParams.date) {
            delete urlParams.timeSlot;
            delete urlParams.rateGroupIds;
            delete urlParams.date;
            localStorage.setItem('urlParams', JSON.stringify(urlParams));
        }
        
        this.tenantId = this.$store.state.tenantId;
        this.iframeStatus = this.$store.state.iframeStatus;
        this.customer = this.$store.state.customer || { name: "", email: "", phone_number: "" };
        this.id = this.$store.state.bookingIds;
        this.idLength = Object.values(this.id).length;
        if (this.idLength) {
            this.booking();
        } else {
            this.$router.push("/");
        }
    },
    mounted() {
        // Run confetti animation after everything has rendered
        this.$nextTick(() => {
            this.initConfetti();
        });
    },
    methods: {
        booking() {
            const loader = this.$loading.show();
            const payload = { ...this.id };

            axios.post("/package-booking-confirmed", { ids: payload }).then((response) => {
                this.tourBooking = response.data;

                this.openAccordions = this.tourBooking.data.map(() => true);

                // Push booking success event to GTM
                if (window.dataLayer) {
                    const bookingData = this.tourBooking.data[0];
                    window.dataLayer.push({
                        event: "thankyou",
                        booking_id: bookingData.id,
                        customer_name: this.customer?.name || "",
                        customer_email: this.customer?.email || "",
                        customer_phone: this.customer?.phone_number || "",
                        tid: this.$store.state.tenantId || null,
                        oid: this.$store.state.tourOperatorId || 0,
                        pid: this.$store.state.packageId || 0,
                        aid: this.$store.state.affiliateId || 0,
                        cids: this.$store.state.comboIds || 0
                    });
                }

                this.$store.dispatch('storeBookingIds', {});
                this.processLoader(loader);
            }).catch((error) => {
                console.error('Error fetching booking confirmation:', error);
                this.processLoader(loader);
            });
        },
        currencyFormat(amount) {
            return formatCurrencyIntl(amount, this.$store.state.currency);
        },
        toggleAccordion(index) {
            this.openAccordions[index] = !this.openAccordions[index];
        },
        processLoader(loader) {
            loader.hide();
        },
        initConfetti() {
            (function() {
                'use strict';
                var random = Math.random,
                    sin = Math.sin,
                    confetti = [],
                    frame;

                var sizeMin = 3,
                    sizeMax = 9,
                    dxThetaMin = -0.1,
                    dxThetaMax = 0.2,
                    dyMin = 0.13,
                    dyMax = 0.18,
                    dThetaMin = 0.4,
                    dThetaMax = 0.3;

                var colors = ['#FFB347', '#00BFA5', '#FF5252', '#9C27B0', '#3F51B5'];

                function Confetto() {
                    this.outer = document.createElement('div');
                    this.inner = document.createElement('div');
                    this.outer.appendChild(this.inner);

                    var outerStyle = this.outer.style, innerStyle = this.inner.style;
                    outerStyle.position = 'absolute';
                    outerStyle.width = (sizeMin + (sizeMax * random())) + 'px';
                    outerStyle.height = (sizeMin + (sizeMax * random())) + 'px';
                    innerStyle.width = '100%';
                    innerStyle.height = '100%';
                    innerStyle.backgroundColor = colors[Math.floor(random() * colors.length)];

                    outerStyle.left = (window.innerWidth * random()) + 'px';
                    outerStyle.top = '-50px';
                    this.x = parseFloat(outerStyle.left);
                    this.y = -50;
                    this.dx = sin(dxThetaMin + dxThetaMax * random());
                    this.dy = dyMin + dyMax * random();
                    this.theta = 360 * random();
                    this.dTheta = dThetaMin + dThetaMax * random();

                    this.update = function() {
                        this.x += this.dx * 16;
                        this.y += this.dy * 16;
                        this.theta += this.dTheta * 16;

                        outerStyle.left = this.x + 'px';
                        outerStyle.top = this.y + 'px';
                        outerStyle.transform = 'rotate(' + this.theta + 'deg)';
                    };
                }

                var container = document.createElement('div');
                container.style.position = 'fixed';
                container.style.top = '0';
                container.style.left = '0';
                container.style.width = '100%';
                container.style.height = '0';
                container.style.overflow = 'visible';
                container.style.zIndex = '9999';
                document.body.appendChild(container);

                // Spawn confetti continuously
                var confettiInterval = setInterval(() => {
                    var c = new Confetto();
                    confetti.push(c);
                    container.appendChild(c.outer);
                }, 100);

                // Animation loop
                function loop() {
                    confetti.forEach(c => c.update());
                    frame = requestAnimationFrame(loop);
                }
                frame = requestAnimationFrame(loop);

                // Stop everything after 5 seconds
                setTimeout(() => {
                    clearInterval(confettiInterval);
                    if (frame) cancelAnimationFrame(frame);
                    confetti.forEach(c => container.removeChild(c.outer));
                    confetti = [];
                    if (container.parentNode) container.parentNode.removeChild(container);
                }, 10000);
            })();
        }
    }
};
</script>